/**
 * Tenant integrations: the outside services a tenant connects with its own access key or login (ADR-016 §1;
 * docs/specs/integrations.md). The catalogue, who may add, replace or remove a stored key or login, what each
 * provider's stored value looks like, and the provider proxy's per-tenant rate limits and cache times.
 *
 * This file has no imports on purpose: the Edge Functions (Deno) use an identical copy,
 * `supabase/functions/_shared/integrations.ts`, and `test/integrations.test.ts` fails if the two differ. Edit this
 * file, then copy it over the Edge Function copy.
 *
 * MB's decisions: keys are per tenant, no shared Braynewave key (decision 6, Q73); tracker logins may be updated by
 * tenant admins AND Field app users, write-only, never read back (Q71, decision 98); the Thomson tenant uses the same
 * provider accounts and tenant admins and managers may replace any stored value (Q72, decision 157); TA Live uses the
 * same key as the Diurnal tool, pasted on the Integrations screen, never copied from code (Q35, decision 156); each
 * tenant stores its own NAIPS login for Australian NOTAMs, used on the server (Q50, decision 77); NAIPS is staff only,
 * clients never trigger a NAIPS call (Q187, decision 213); each tenant stores its own OpenSky API client (client id
 * and secret), exchanged on the server for a short access token, staff only (Q296, decision 354).
 */

export type IntegrationProviderId =
  | 'tracplus'
  | 'v2track'
  | 'websentinel'
  | 'spidertracks'
  | 'openaip'
  | 'naips'
  | 'opensky'
  | 'openweathermap'
  | 'checkwx'
  | 'wunderground'
  | 'windy'
  | 'talive'
  | 'nextcloud'
  | 'bom_sws'
  | 'space_weather'
  | 'docuseal'
  | 'anthropic';

/**
 * What the tenant stores: an access key, a username and password, or a feed address with the login inside it;
 * `none` for a public service the proxy asks without any stored value (the space weather sources, decision 687:
 * nothing is set up, nothing is counted against a key).
 */
export type CredentialKind = 'api_key' | 'login' | 'feed_url' | 'none';

export type ProviderCategory =
  'tracker' | 'aviation' | 'weather' | 'sensor' | 'archive' | 'space_weather' | 'signing' | 'reading';

/** How the proxy returns an operation's answer. */
export type OpBody = 'json' | 'text' | 'binary';

export interface ProviderOpDef {
  /** Seconds a successful answer is kept per tenant and reused for every caller of that tenant (0: not kept). */
  readonly cacheSeconds: number;
  readonly body: OpBody;
}

export interface IntegrationProviderDef {
  readonly id: IntegrationProviderId;
  readonly name: string;
  readonly category: ProviderCategory;
  readonly credential: CredentialKind;
  /** A tenant may connect several accounts of a tracker (each with its own label); one key for the others. */
  readonly multipleAccounts: boolean;
  /** Where the tenant admin finds the key or login, in the help voice. */
  readonly whereToFind: string;
  /** Calls per minute one tenant may make through the proxy (Teslyn's own limit, protecting the tenant's quota). */
  readonly callsPerMinute: number;
  /** Named operations the proxy performs; there is no free-form address (inventory 03 §4 S5). */
  readonly ops: Readonly<Record<string, ProviderOpDef>>;
  /**
   * Only tenant staff may use it, even where clients may use its category (NAIPS, decision 213). The database's
   * `integration_checkout` and the proxy's own check both refuse a client.
   */
  readonly staffOnly?: boolean;
  /**
   * The names of a login's two fields and of the whole when they are not a username and password (OpenSky's API
   * client: "Client ID", "Client secret", "API client").
   */
  readonly loginLabels?: { readonly username: string; readonly password: string; readonly what: string };
  /**
   * The organisation's switch the provider needs (`tenant_service_switch.service`), set by Braynewave's platform
   * admins: the proxy refuses every call while it is off, before any provider call (decision 469: Weather Underground
   * and Windy webcams, on for Thomson Airborne only).
   */
  readonly tenantSwitch?: TenantServiceSwitch;
}

/**
 * The per-organisation switches of `tenant_service_switch` (20261020110000, 20261022000600): adsb.fi (decision 405),
 * Weather Underground stations and Windy webcams (decision 469). A missing row is off.
 */
export type TenantServiceSwitch = 'adsbfi' | 'weather_stations' | 'webcams';
export const TENANT_SERVICE_SWITCHES: readonly TenantServiceSwitch[] = [
  'adsbfi',
  'weather_stations',
  'webcams',
];

const op = (cacheSeconds: number, body: OpBody = 'json'): ProviderOpDef => ({ cacheSeconds, body });

/**
 * The providers of the Diurnal tool that need a key or login (inventory 03 §2, 05 §1 item 2, F14), in the order the
 * Integrations screen lists them. Cache times follow the Diurnal poll intervals so one upstream call serves every
 * laptop and browser of the tenant within one poll: V2Track 10 s, Spider Tracks and WebSentinel 30 s, TracPlus 60 s.
 */
export const INTEGRATION_PROVIDERS: readonly IntegrationProviderDef[] = [
  {
    id: 'v2track',
    name: 'V2Track',
    category: 'tracker',
    credential: 'login',
    multipleAccounts: true,
    whereToFind: 'The username and password you use to sign in to the V2Track website.',
    callsPerMinute: 60,
    ops: {
      positions: op(10),
      assets: op(300),
      alerts: op(30),
      trip: op(60),
      history: op(120),
      trips: op(120),
    },
  },
  {
    id: 'spidertracks',
    name: 'Spider Tracks',
    category: 'tracker',
    credential: 'login',
    multipleAccounts: true,
    whereToFind: 'The email address and password you use to sign in to Spidertracks.',
    callsPerMinute: 30,
    ops: { latest: op(30), tracks: op(120), track: op(120) },
  },
  {
    id: 'websentinel',
    name: 'WebSentinel',
    category: 'tracker',
    credential: 'login',
    multipleAccounts: true,
    whereToFind:
      'The username and password you use to sign in to WebSentinel (Latitude Technologies, SkyTrac).',
    callsPerMinute: 20,
    ops: { positions: op(30), history: op(120, 'text') },
  },
  {
    id: 'tracplus',
    name: 'TracPlus',
    category: 'tracker',
    credential: 'feed_url',
    multipleAccounts: true,
    whereToFind:
      'The KML network link address from TracPlus (it starts with https:// and contains your login, so treat it as a password).',
    callsPerMinute: 10,
    ops: { feed: op(60, 'text') },
  },
  {
    id: 'openaip',
    name: 'OpenAIP',
    category: 'aviation',
    credential: 'api_key',
    multipleAccounts: false,
    whereToFind: 'The API key of your organisation’s account at openaip.net (Profile, API clients).',
    callsPerMinute: 600,
    // tile: the "openaip" raster chart; the area lists draw the aviation layers (docs/specs/map.md §19), each kept a
    // day for the tenant (OpenAIP rate-limits them hard; airspace changes with the 28-day AIRAC cycle); airspace: one
    // airspace in full
    ops: {
      tile: op(0, 'binary'),
      airspaces: op(86_400),
      airspace: op(3600),
      airports: op(86_400),
      navaids: op(86_400),
      reporting_points: op(86_400),
    },
  },
  {
    // MB decision 77 (Q50): each tenant stores its own NAIPS login, used by Teslyn on the server for Australian
    // NOTAMs. One sign-in and one area briefing per call, as the Diurnal tool does (inventory 04 §3.4); a briefing
    // is kept an hour for the tenant, as the Diurnal tool asks hourly (decision 215, Q189), and the limit stays low
    // because each call signs in to NAIPS again. Location (aerodrome) briefings with MET (decisions 214, 217) post
    // the form learned from the real site with location_form, a read-only capture never kept (issue #25).
    id: 'naips',
    name: 'NAIPS',
    category: 'aviation',
    credential: 'login',
    multipleAccounts: false,
    whereToFind:
      'The username and password you use to sign in to NAIPS, Airservices Australia’s pilot briefing service (www.airservicesaustralia.com/naips). Registration there is free.',
    callsPerMinute: 6,
    ops: { area_briefing: op(3600, 'text'), location_briefing: op(3600, 'text'), location_form: op(0) },
    // MB decision 213 (Q187): staff only; clients never trigger a NAIPS call
    staffOnly: true,
  },
  {
    // MB decision 354 (Q296): the organisation's own OpenSky API client (OAuth2 client credentials; OpenSky no longer
    // accepts a username and password). The proxy exchanges it for an access token of 30 minutes, kept in the
    // provider session and handed to the tenant's laptops, which ask OpenSky themselves (docs/specs/tracker.md
    // §5.2): the client secret never leaves Vault and the positions never pass through Teslyn's server. Staff only,
    // like the tracker accounts; a token is asked about every half hour per laptop, so the limit is low.
    id: 'opensky',
    name: 'OpenSky',
    category: 'aviation',
    credential: 'login',
    multipleAccounts: false,
    whereToFind:
      'An API client of your organisation’s OpenSky Network account: sign in at opensky-network.org, open your Account page (https://opensky-network.org/my-opensky/account), create an API client and copy its client ID and client secret.',
    callsPerMinute: 10,
    ops: { token: op(0) },
    staffOnly: true,
    loginLabels: { username: 'Client ID', password: 'Client secret', what: 'API client' },
  },
  {
    id: 'openweathermap',
    name: 'OpenWeatherMap',
    category: 'weather',
    credential: 'api_key',
    multipleAccounts: false,
    whereToFind: 'The API key of your organisation’s account at openweathermap.org (My API keys).',
    callsPerMinute: 600,
    // tile: map overlays; weather: the planning wind at a point (item 04-037), cached 10 minutes per tenant
    ops: { tile: op(0, 'binary'), weather: op(600) },
  },
  {
    id: 'checkwx',
    name: 'CheckWX',
    category: 'weather',
    credential: 'api_key',
    multipleAccounts: false,
    whereToFind: 'The API key of your organisation’s account at checkwxapi.com.',
    callsPerMinute: 30,
    // CheckWX asks for METARs to be kept at least 15 minutes (checkwxapi.com documentation, 20 Oct 2026)
    ops: { metar: op(900), metar_near: op(900) },
  },
  {
    id: 'wunderground',
    name: 'Weather Underground',
    category: 'weather',
    credential: 'api_key',
    multipleAccounts: false,
    whereToFind:
      'The API key of your organisation’s Weather Underground account: sign in at wunderground.com, open Member settings, API keys (the key of a personal weather station’s owner).',
    // decision 469: Thomson Airborne only, behind its switch, while MB seeks The Weather Company's written authority.
    // The key allows 30 calls a minute and 1,500 a day (Weather Underground's PWS API notice); the nearby list is kept
    // an hour and an observation 10 minutes (its own max-age is 599 s), so laptops near each other share them
    callsPerMinute: 30,
    ops: { stations_near: op(3600), observation: op(600) },
    tenantSwitch: 'weather_stations',
  },
  {
    id: 'windy',
    name: 'Windy webcams',
    category: 'weather',
    credential: 'api_key',
    multipleAccounts: false,
    whereToFind:
      'The Webcams API key of your organisation’s account at api.windy.com (sign in, Keys, a key for the Webcams API).',
    // decision 469: Thomson Airborne only, behind its switch, while MB seeks Windy's written authority. The image
    // links of an answer expire 10 minutes after Windy gives them (free tier), so an answer is kept 5 minutes at most
    // and Teslyn Field shows an image only while its link has a minute left
    callsPerMinute: 30,
    ops: { webcams_near: op(300) },
    tenantSwitch: 'webcams',
  },
  {
    id: 'talive',
    name: 'TA Live',
    category: 'sensor',
    credential: 'api_key',
    multipleAccounts: false,
    whereToFind:
      'The API key of the TA Live project, the same one the Diurnal QC Tool uses (MB decision 156): in the TA Live Supabase dashboard, Project settings, API keys.',
    callsPerMinute: 120,
    ops: {
      day: op(30),
      max_id: op(60),
      latest: op(60),
      // the station-health poller's new rows of one station (parity C2): never kept, each answer is new
      since: op(0),
      // read-only views of the set-up for the migration study (decision 183)
      recent: op(60),
      oldest: op(3600),
      count: op(3600),
    },
  },
  {
    // MB decision 376 (Q319): a tenant's file archive may be its own Nextcloud, over WebDAV. The login is the
    // Nextcloud username and an app password (never the account password), held in Vault and used only by the Edge
    // Function archive-upload, which forwards Teslyn Field's archive uploads (docs/specs/cloud/file-archive.md). No
    // provider-proxy operation: the proxy never calls a Nextcloud. The server address and folder are kept beside it
    // in tenant_archive.
    id: 'nextcloud',
    name: 'Nextcloud',
    category: 'archive',
    credential: 'login',
    multipleAccounts: false,
    whereToFind:
      'Your Nextcloud username and an app password made for Teslyn: in Nextcloud, open Settings, Security, type “Teslyn” under “App name” and press “Create new app password”. Use the app password, never your own password.',
    callsPerMinute: 600,
    ops: {},
    loginLabels: { username: 'username', password: 'app password', what: 'login' },
  },
  {
    // MB decision 687 (Q617): each organisation stores its own Bureau of Meteorology Space Weather API key
    // (registration at sws-data.sws.bom.gov.au/register, free); Teslyn Cloud asks BoM through the proxy and keeps
    // each answer 15 minutes for the organisation, so every laptop shares one call. Staff only (decisions 688, 691).
    // BoM updates its K index every 20 minutes (docs/specs/space-weather.md §3.1).
    id: 'bom_sws',
    name: 'BoM Space Weather',
    category: 'space_weather',
    credential: 'api_key',
    multipleAccounts: false,
    whereToFind:
      'The API key the Bureau of Meteorology emailed your organisation after registering at sws-data.sws.bom.gov.au/register (Space Weather API; free).',
    callsPerMinute: 30,
    ops: {
      k_index: op(900),
      a_index: op(3600),
      dst_index: op(900),
      mag_alert: op(900),
      mag_warning: op(900),
      aurora_alert: op(900),
      aurora_watch: op(900),
      aurora_outlook: op(3600),
    },
    staffOnly: true,
  },
  {
    // The public space weather sources (docs/specs/space-weather.md §3; decisions 687 to 694): NOAA SWPC, NASA CCMC
    // DONKI, spaceweather.com (with permission, decision 689), the BoM website's pc3 and GEOSTAT pictures (694) and
    // the reading list's news pages (693). No key: nothing to set up, nothing stored, no call count; each answer is
    // kept for the organisation as long as below (NOAA asks requests to follow its refresh rates). Staff only.
    id: 'space_weather',
    name: 'Public space weather sources',
    category: 'space_weather',
    credential: 'none',
    multipleAccounts: false,
    whereToFind: 'Nothing to set up: NOAA, NASA and the other public sources need no key.',
    callsPerMinute: 120,
    ops: {
      noaa_scales: op(900),
      noaa_kp_1m: op(900),
      noaa_alerts: op(900),
      noaa_solar_wind_now: op(900),
      noaa_kp: op(1800),
      noaa_kp_forecast: op(1800),
      noaa_forecast_3day: op(1800, 'text'),
      noaa_geomag_forecast: op(1800, 'text'),
      noaa_discussion: op(1800, 'text'),
      noaa_solar_wind_24h: op(1800),
      noaa_enlil_frames: op(1800),
      donki_enlil: op(1800),
      donki_cme_analysis: op(1800),
      donki_notifications: op(3600),
      spaceweather_com: op(900),
      bom_pictures: op(1200),
      bom_site_news: op(3600),
      swpc_news: op(3600),
      nasa_blog: op(3600),
    },
    staffOnly: true,
  },
  {
    // MB's R120 and decisions 133, 158 (Q117): the organisation's own DocuSeal account (cloud plan with API access)
    // signs the JSP safety meeting, later the JSA and contracts (docs/specs/cloud/docuseal.md). The key is used only
    // by the Edge Function `docuseal`, never through the proxy (no operation) and never on a device. Each send,
    // check and notification counts one call; DocuSeal's own limits are far above 30 a minute.
    id: 'docuseal',
    name: 'DocuSeal',
    category: 'signing',
    credential: 'api_key',
    multipleAccounts: false,
    whereToFind:
      'The API key of your organisation’s DocuSeal account: sign in at docuseal.com, open Settings, API, and copy the key (the cloud plan with API access).',
    callsPerMinute: 30,
    ops: {},
    staffOnly: true,
  },
  {
    // F03, F09, decision 507 (Q447 B): the organisation's own Claude API key reads an MR page, a fuel docket or a
    // receipt from a photo (docs/specs/document-reading.md §2). Used only by the Edge Function `document-read`, never
    // through the proxy and never on a device. Each picture read counts one call.
    id: 'anthropic',
    name: 'Claude (Anthropic)',
    category: 'reading',
    credential: 'api_key',
    multipleAccounts: false,
    whereToFind:
      'An API key of your organisation’s Anthropic account: sign in at console.anthropic.com, open API keys, and create a key for Teslyn. Reading a page costs a few cents, billed by Anthropic to your account.',
    callsPerMinute: 10,
    ops: {},
    staffOnly: true,
  },
];

export function integrationProvider(id: string): IntegrationProviderDef | null {
  return INTEGRATION_PROVIDERS.find((p) => p.id === id) ?? null;
}

// ---------------------------------------------------------------------------------------------- who may do what

const STAFF_ROLES = ['tenant_admin', 'manager', 'field_crew', 'office_staff', 'data_processor'] as const;

/** Staff roles that may replace a stored tracker login (MB Q71: "Field app users"; managers may replace any value). */
export const LOGIN_UPDATER_ROLES = ['tenant_admin', 'manager', 'field_crew', 'data_processor'] as const;

const anyOf = (roles: readonly string[], wanted: readonly string[]): boolean =>
  roles.some((r) => wanted.includes(r));

/** Add a provider, or remove one: tenant admin only (ADR-016 §1). */
export function canManageIntegrations(roles: readonly string[]): boolean {
  return roles.includes('tenant_admin');
}

/**
 * Replace the stored value of an existing integration: tenant admins and managers for every provider (Q72, decision
 * 157); Field users (field crew, data processor) also for tracker logins, because many of those passwords change
 * regularly (Q71). Office staff are read-only everywhere.
 */
export function canReplaceCredential(roles: readonly string[], provider: IntegrationProviderDef): boolean {
  if (anyOf(roles, ['tenant_admin', 'manager'])) return true;
  return provider.category === 'tracker' && anyOf(roles, LOGIN_UPDATER_ROLES);
}

/**
 * Use a provider through the proxy: any tenant staff member; a client user of the tenant only for aviation and
 * weather (map overlays on the client map), except a staff-only provider (NAIPS, decision 213). Tracker accounts and
 * TA Live are never called for a client: clients see positions and readings the cloud has already filtered by
 * entitlement (Q37, Q47).
 */
export function canUseProvider(
  access: { readonly tenantRoles: readonly string[]; readonly isClientOfTenant: boolean },
  provider: IntegrationProviderDef,
): boolean {
  if (anyOf(access.tenantRoles, STAFF_ROLES)) return true;
  if (provider.staffOnly) return false;
  return access.isClientOfTenant && (provider.category === 'aviation' || provider.category === 'weather');
}

// ---------------------------------------------------------------------------------------------- stored values

export type CredentialSecret =
  | { readonly kind: 'api_key'; readonly apiKey: string }
  | { readonly kind: 'login'; readonly username: string; readonly password: string }
  | { readonly kind: 'feed_url'; readonly url: string };

export type Checked<T> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: string };

/** Hosts a TracPlus feed address may point at (the Diurnal placeholder is `reports.api.tracplus.com/kml?…`). */
const TRACPLUS_HOST = /(^|\.)tracplus\.com$/i;

function text(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * Check the value a tenant admin or Field user typed for `provider`. Values are trimmed; nothing is stored or
 * logged here. The error names the field, never the value.
 */
export function checkCredential(provider: IntegrationProviderDef, input: unknown): Checked<CredentialSecret> {
  const b = input && typeof input === 'object' ? (input as Record<string, unknown>) : {};
  switch (provider.credential) {
    case 'api_key': {
      const apiKey = text(b.api_key);
      if (apiKey.length < 8 || apiKey.length > 512 || /\s/.test(apiKey)) {
        return { ok: false, error: `Enter the ${provider.name} access key: 8 to 512 characters, no spaces.` };
      }
      return { ok: true, value: { kind: 'api_key', apiKey } };
    }
    case 'login': {
      const username = text(b.username);
      const password = typeof b.password === 'string' ? b.password : '';
      const names = provider.loginLabels ?? { username: 'username', password: 'password' };
      if (!username || username.length > 256) {
        return { ok: false, error: `Enter the ${provider.name} ${names.username} (up to 256 characters).` };
      }
      if (!password || password.length > 1024) {
        return { ok: false, error: `Enter the ${provider.name} ${names.password} (up to 1024 characters).` };
      }
      return { ok: true, value: { kind: 'login', username, password } };
    }
    case 'none':
      return { ok: false, error: `${provider.name} needs nothing stored.` };
    case 'feed_url': {
      const raw = text(b.url);
      let u: URL;
      try {
        u = new URL(raw);
      } catch {
        return { ok: false, error: `Enter the full ${provider.name} feed address, starting with https://.` };
      }
      if (u.protocol !== 'https:' || !TRACPLUS_HOST.test(u.hostname) || raw.length > 2048) {
        return {
          ok: false,
          error: `The ${provider.name} feed address must start with https:// and be on tracplus.com.`,
        };
      }
      return { ok: true, value: { kind: 'feed_url', url: raw } };
    }
  }
}

/**
 * The four characters shown beside a stored value so people can tell which one is stored: the end of the key, of
 * the username for a login, or of the feed address. Never more than four characters, never the password.
 */
export function lastFour(secret: CredentialSecret): string {
  const s =
    secret.kind === 'api_key' ? secret.apiKey : secret.kind === 'login' ? secret.username : secret.url;
  return s.slice(-4);
}

/** The stored value as one string for Vault (JSON), and back. */
export function encodeSecret(secret: CredentialSecret): string {
  return JSON.stringify(secret);
}

export function decodeSecret(stored: string): CredentialSecret | null {
  try {
    const v = JSON.parse(stored) as Record<string, unknown>;
    if (v.kind === 'api_key' && typeof v.apiKey === 'string') return { kind: 'api_key', apiKey: v.apiKey };
    if (v.kind === 'login' && typeof v.username === 'string' && typeof v.password === 'string') {
      return { kind: 'login', username: v.username, password: v.password };
    }
    if (v.kind === 'feed_url' && typeof v.url === 'string') return { kind: 'feed_url', url: v.url };
  } catch {
    /* not JSON */
  }
  return null;
}

/** Every part of a stored value that must never appear in an answer, a log line or an error (longest first). */
export function secretParts(secret: CredentialSecret): string[] {
  let parts: string[];
  if (secret.kind === 'api_key') parts = [secret.apiKey, encodeURIComponent(secret.apiKey)];
  else if (secret.kind === 'login') {
    parts = [
      secret.password,
      encodeURIComponent(secret.password),
      secret.username,
      encodeURIComponent(secret.username),
    ];
  } else {
    parts = [secret.url];
    try {
      const u = new URL(secret.url);
      parts.push(
        u.search,
        ...u.search
          .slice(1)
          .split('&')
          .map((kv) => kv.split('=')[1] ?? ''),
      );
    } catch {
      /* checked on entry; nothing more to remove */
    }
  }
  return [...new Set(parts.filter((p) => p.length >= 4))].sort((a, b) => b.length - a.length);
}

/** Replace every secret part in `body` with "[removed]" (the proxy's last safeguard before it answers). */
export function redactSecrets(body: string, parts: readonly string[]): string {
  let out = body;
  for (const p of parts) if (p) out = out.split(p).join('[removed]');
  return out;
}

// ---------------------------------------------------------------------------------------------- status words

/** `tenant_integration.status`: working, or the provider refused the stored value on the last call. */
export type IntegrationStatus = 'active' | 'failing';

export interface IntegrationRow {
  readonly id: string;
  readonly tenant_id: string;
  readonly provider: string;
  readonly label: string;
  readonly kind: CredentialKind;
  readonly status: IntegrationStatus;
  readonly last_four: string;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly last_used_at: string | null;
  readonly last_error: string | null;
  readonly monthly_calls: number;
  readonly calls_month: string | null;
}

/** "Working", "Not working: …", or "Not set up" for a provider without a stored value. */
export function integrationStatusText(row: Pick<IntegrationRow, 'status' | 'last_error'> | null): string {
  if (!row) return 'Not set up';
  if (row.status === 'failing')
    return `Not working: ${row.last_error ?? 'the provider refused the stored value'}`;
  return 'Working';
}

/** The words of a feature that needs a provider the tenant has not set up, with who can fix it (ADR-016 §1). */
/** The proxy's refusal while the provider's organisation switch is off (decision 469). */
export function switchedOffText(provider: IntegrationProviderDef): string {
  return `${provider.name} is not switched on for this organisation. Braynewave switches it on once the arrangement with the provider is in place.`;
}

/** Whether `rows` (the organisation's `tenant_service_switch` rows) switch `provider` on; true for a provider without one. */
export function tenantSwitchOn(provider: IntegrationProviderDef, rows: unknown): boolean {
  if (!provider.tenantSwitch) return true;
  return (
    Array.isArray(rows) &&
    rows.some(
      (r) =>
        r !== null &&
        typeof r === 'object' &&
        (r as { service?: unknown }).service === provider.tenantSwitch &&
        (r as { enabled?: unknown }).enabled === true,
    )
  );
}

export function notSetUpText(provider: IntegrationProviderDef): string {
  return `${provider.name} is not set up. A tenant admin adds your organisation’s own ${credentialWord(
    provider,
  )} in Teslyn Cloud, Integrations.`;
}

/** What the stored value is called in words: "access key", "login", or the provider's own ("API client"). */
export function credentialWord(provider: IntegrationProviderDef): string {
  return provider.credential === 'api_key' ? 'access key' : (provider.loginLabels?.what ?? 'login');
}

/** Calls this month, counting from zero when the stored month is not the current one. */
export function callsThisMonth(
  row: Pick<IntegrationRow, 'monthly_calls' | 'calls_month'>,
  now: Date,
): number {
  const month = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
  return row.calls_month?.startsWith(month) ? row.monthly_calls : 0;
}
